import React, { PropsWithChildren, ReactElement } from 'react'
import { Divider, Flex, Text } from 'theme-ui'
import { Button, Svg } from 'ui'
import EasyToSetupIcon from 'images/organization/industries/schools/easy-to-setup.svg'
import { navigate } from 'gatsby'
import { TextWithBreakpointVariant } from 'ui/TextWithBreakpointVariant'
import LogoIcon from 'images/organization/industries/schools/logo.svg'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'

export default function EasyToSetupContainer({
  title,
  subTitle,
  question,
  suggestion,
  children,
}: PropsWithChildren<{
  title: string
  subTitle?: string
  question: string
  suggestion?: string
}>): ReactElement {
  const isMobileOrTablet = useBreakpointIndex() < 2

  return (
    <Flex
      sx={{
        position: 'relative',
        width: '100%',
        py: ['8rem', '12rem'],
        px: '1.6rem',
        mt: ['8rem', '12rem'],
        justifyContent: 'center',
        backgroundColor: 'smokyBlack',
        borderTop: '1px solid',
        borderTopColor: 'blueYonder20',
        borderBottom: '1px solid',
        borderBottomColor: 'blueYonder20',
        '& > svg': {
          visibility: isMobileOrTablet ? 'hidden' : 'visible',
        },
      }}
    >
      <Svg
        svg={LogoIcon}
        descriptiveText="Logo icon"
        sx={{ position: 'absolute', bottom: 0, left: 0, zIndex: 'zIndex0' }}
      />
      <Flex
        sx={{
          width: '100%',
          maxWidth: '102.2rem',
          gap: '8rem',
          justifyContent: 'center',
          flexDirection: ['column', 'row'],
          zIndex: 'zIndex1',
        }}
      >
        <Flex
          sx={{ width: '100%', flexDirection: 'column', gap: '2.4rem', justifyContent: 'center' }}
        >
          <Svg svg={EasyToSetupIcon} descriptiveText="Easy to Setup" />
          <Flex sx={{ flexDirection: 'column', gap: '0.8rem' }}>
            <Text variant="size32Weight600" sx={{ color: 'aliceBlue' }}>
              {title}
            </Text>
            <Text variant="size15Weight400Line138Letter06" sx={{ color: 'wildBlueYonder' }}>
              {subTitle}
            </Text>
          </Flex>
          <Divider
            sx={{
              width: '100%',
              height: '1px',
              color: 'blueYonder8',
              my: 0,
            }}
          />
          <Flex sx={{ flexDirection: 'column', gap: '0.8rem' }}>
            <Text variant="size17Weight600Line140" sx={{ color: 'aliceBlue' }}>
              {question}
            </Text>
            {suggestion && (
              <Text variant="size17Weight600Line140" sx={{ color: 'aliceBlue' }}>
                {suggestion}
              </Text>
            )}
          </Flex>
          <Button
            ariaLabel="Book a Demo"
            name="Book a Demo"
            data-testid="book-demo-button"
            sx={{
              display: 'inline-flex',
              flexShrink: 0,
              width: ['12.9rem', 'fit-content'],
              py: '0.8rem',
              px: ['0.8rem', '1.6rem'],
              height: ['4rem', '3.9rem'],
              minWidth: ['12.9rem', '15rem'],
              whiteSpace: 'nowrap',
              '@media (max-width: 324px)': {
                width: '100%',
              },
            }}
            variant="roundedSecondary"
            onClick={() => navigate('/demo')}
          >
            <TextWithBreakpointVariant
              variant={['size16Weight600Line138', 'size17Weight600Line138']}
            >
              Book a Demo
            </TextWithBreakpointVariant>
          </Button>
        </Flex>
        <Flex sx={{ flexDirection: 'column', gap: '2.4rem', flexShrink: 0 }}>{children}</Flex>
      </Flex>
    </Flex>
  )
}

export function SetupStep({
  order,
  title,
  description,
}: {
  order: number
  title: string
  description: string
}): ReactElement {
  return (
    <Flex
      sx={{
        width: ['100%', '40rem'],
        p: '2.4rem',
        flexDirection: 'column',
        borderRadius: '1.2rem',
        backgroundColor: 'eerieBlack3',
      }}
    >
      <Flex
        sx={{
          width: '3.2rem',
          height: '3.2rem',
          alignItems: 'center',
          justifyContent: 'center',
          border: '0.6px solid',
          borderRadius: '0.6rem',
          borderColor: 'veronica',
          backgroundColor: 'veronica10',
          flexShrink: 0,
        }}
      >
        <Text variant="size13Weight500Line136Letter013" sx={{ color: 'aliceBlue' }}>
          {order}
        </Text>
      </Flex>
      <Text variant="size16Weight600Line138" sx={{ color: 'aliceBlue', mt: '1.2rem' }}>
        {title}
      </Text>
      <Text variant="size15Weight400Line138Letter03" sx={{ color: 'lightSteelBlue', mt: '0.4rem' }}>
        {description}
      </Text>
    </Flex>
  )
}
